import { Modal, MoreMenu, Text } from '@shared/components';
import { RatingMethod } from '@shared/generated/graphql';
import useMovement from '@shared/graphql/hooks/movements/useMovement';
import { formatTime } from '@shared/plugin/utils/datetime';
import { CarrierRate } from '@shared/types/quote';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { sumCarrierRate } from '@shared/utils/rates/rates';
import { isNil } from 'lodash';
import React from 'react';

const ROOT = makeRootClassName('CarrierOffers');
export const el = makeElementClassNameFactory(ROOT);

type CarrierOffersProps = {
  movementId: string;
  carrierOfferActions: {
    label: string;
    onPress: (offer: CarrierRate) => void;
  }[];
};
export const CarrierOffers = ({
  movementId,
  carrierOfferActions,
}: CarrierOffersProps) => {
  const { movement } = useMovement({ id: movementId, pollInterval: 5000 });

  if (isNil(movement)) return null;

  const sortedOffers = movement.carrierRates
    .filter((r) => r.ratingMethod === RatingMethod.DIRECT_OFFER)
    .sort((a, b) => {
      return sumCarrierRate(b) - sumCarrierRate(a);
    });

  const offers =
    sortedOffers.length === 0 ? (
      <Text type="body-xs" className="italic text-gray-400">
        No offers have been collected
      </Text>
    ) : (
      sortedOffers.map((offer) => (
        <CarrierOfferCard
          key={offer.id}
          {...toCarrierOfferCardProps(offer, carrierOfferActions)}
        />
      ))
    );

  return (
    <div className="flex flex-col gap-3">
      <Text type="body-sm" className="text-gray-700">
        Carrier offers
      </Text>
      <div className="flex max-h-[250px] flex-col gap-2 overflow-y-scroll">
        {offers}
      </div>
    </div>
  );
};

function toCarrierOfferCardProps(
  offer: CarrierRate,
  carrierOfferActions: {
    label: string;
    onPress: (offer: CarrierRate) => void;
  }[]
): CarrierOfferCardProps {
  return {
    createdAt: new Date(offer.createdAt),
    carrierName: offer.carrier?.name || 'Unknown Carrier',
    offer,
    carrierOfferActions,
  };
}

type CarrierOfferCardProps = {
  createdAt: Date;
  carrierName: string;
  offer: CarrierRate;
  carrierOfferActions: {
    label: string;
    onPress: (offer: CarrierRate) => void;
  }[];
};
function CarrierOfferCard({
  createdAt,
  carrierName,
  offer,
  carrierOfferActions,
}: CarrierOfferCardProps) {
  const [isCarrierDetailsOpen, setIsCarrierDetailsOpen] = React.useState(false);
  const [isContactDetailsOpen, setIsContactDetailsOpen] = React.useState(false);
  const options = [
    {
      label: 'View carrier details',
      onPress: () => setIsCarrierDetailsOpen(true),
    },
    {
      label: 'View contact details',
      onPress: () => setIsContactDetailsOpen(true),
    },
    ...carrierOfferActions.map((action) => ({
      label: action.label,
      onPress: () => action.onPress(offer),
    })),
  ];
  return (
    <>
      <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row gap-1">
            <Text type="body-xs" isHeavy>
              ${sumCarrierRate(offer)}
            </Text>
            <Text
              type="body-xs"
              className="line-clamp-1 overflow-hidden text-gray-800"
            >
              • {carrierName}
            </Text>
          </div>
          <div className="flex flex-row items-center gap-2">
            <Text type="body-xs" className="text-gray-500">
              {formatTime(createdAt)}
            </Text>
            {options.length > 0 && <MoreMenu options={options} />}
          </div>
        </div>
      </div>
      <Modal.Root
        open={isCarrierDetailsOpen}
        onOpenChange={setIsCarrierDetailsOpen}
      >
        <Modal.Content size="medium">
          <Modal.Header title="Carrier Details" />
          <div className="p-4">
            {offer.carrier ? (
              <div className="space-y-2">
                <div>
                  <Text type="body-sm" className="text-gray-500">
                    Carrier Name
                  </Text>
                  <Text type="body-md">{offer.carrier?.name || 'N/A'}</Text>
                </div>
                <div>
                  <Text type="body-sm" className="text-gray-500">
                    DOT Number
                  </Text>
                  <Text type="body-md">
                    {offer.carrier?.dotNumber || 'N/A'}
                  </Text>
                </div>
                {offer.carrier?.mcNumber && (
                  <div>
                    <Text type="body-sm" className="text-gray-500">
                      MC Number
                    </Text>
                    <Text type="body-md">{offer.carrier.mcNumber}</Text>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Text type="body-sm" className="text-gray-500">
                  No carrier recorded for this offer
                </Text>
              </div>
            )}
          </div>
          <Modal.Footer>
            <div className="px-2">
              <Modal.FooterButton isCloseButton>Close</Modal.FooterButton>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
      <Modal.Root
        open={isContactDetailsOpen}
        onOpenChange={setIsContactDetailsOpen}
      >
        <Modal.Content size="medium">
          <Modal.Header title="Contact Details" />
          <div className="p-4">
            {offer.contact ? (
              <div className="space-y-2">
                <div>
                  <Text type="body-sm" className="text-gray-500">
                    Name
                  </Text>
                  <Text type="body-md">{offer.contact.name || 'N/A'}</Text>
                </div>
                <div>
                  <Text type="body-sm" className="text-gray-500">
                    Email
                  </Text>
                  <Text type="body-md">{offer.contact.email || 'N/A'}</Text>
                </div>
                <div>
                  <Text type="body-sm" className="text-gray-500">
                    Phone
                  </Text>
                  <Text type="body-md">{offer.contact.phone || 'N/A'}</Text>
                </div>
              </div>
            ) : (
              <div>
                <Text type="body-sm" className="text-gray-500">
                  No contact information available for this offer
                </Text>
              </div>
            )}
          </div>
          <Modal.Footer>
            <div className="px-2">
              <Modal.FooterButton isCloseButton>Close</Modal.FooterButton>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
